import { Vector3 } from '@babylonjs/core';
import { EnemyDistributionConfig } from '../EnemyGenerator';
import { Level } from '../Level';
import { ENEMY_ANIMATIONS, EnemyBehavior } from './EnemyBehavior';

/**
 * Teleport Behavior – disappears with particles, reappears near player, attacks, then retreats.
 *
 * Config fields:
 *   teleportRange    – max distance from player to reappear (default 5)
 *   teleportInterval – cooldown between teleports (default 4)
 *   detectionRange   – range at which teleporting starts (default 25)
 */
export class EnemyTeleportBehavior extends EnemyBehavior {
  private state: 'idle' | 'teleporting' | 'attacking' | 'cooldown' = 'idle';
  private timer: number = 0;
  private teleportDelay: number = 0.4; // brief flash before appearing
  private attackDuration: number = 1.5;
  private hiddenPosition: Vector3 | null = null;

  constructor(startPosition: Vector3, config: EnemyDistributionConfig) {
    super('enemyTeleport', startPosition, config);
  }

  initLevel(level: Level): EnemyTeleportBehavior {
    super.initLevel(level);
    return this;
  }

  behaviorUpdate(deltaTime: number): void {
    if (!this.level?.player) return;

    const dist = this.getPlayerDistance();
    const detRange = this.config.detectionRange ?? 25;
    const cooldown = this.config.teleportInterval ?? 4;

    switch (this.state) {
      case 'idle': {
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
        this.snapToGround(deltaTime);
        if (dist <= detRange) {
          this.beginTeleport();
        }
        break;
      }
      case 'teleporting': {
        // Enemy is hidden; wait then reappear
        this.timer -= deltaTime;
        if (this.timer <= 0) {
          this.reappear();
        }
        break;
      }
      case 'attacking': {
        this.playAnimation(ENEMY_ANIMATIONS.ATTACK_MELEE_RIGHT, true);
        this.lookAt(this.getPlayerPosition());
        this.timer -= deltaTime;
        if (this.timer <= 0) {
          // Disappear again
          this.state = 'cooldown';
          this.timer = cooldown;
          this.teleportAway();
        }
        break;
      }
      case 'cooldown': {
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
        this.snapToGround(deltaTime);
        this.timer -= deltaTime;
        if (this.timer <= 0) {
          this.state = dist <= detRange ? 'idle' : 'idle';
          this.mesh.setEnabled(true);
          this.mesh.position.copyFrom(this.hiddenPosition ?? this.startPosition);
        }
        break;
      }
    }
  }

  private beginTeleport(): void {
    if (!this.level) return;

    this.hiddenPosition = this.mesh.position.clone();

    // Disappear effect
    this.level.particles.createDisappearanceParticleSystem(this.mesh);
    this.level.game.sound.play('checkpoint', this.mesh);

    this.mesh.setEnabled(false);
    this.state = 'teleporting';
    this.timer = this.teleportDelay;
  }

  private reappear(): void {
    if (!this.level) return;

    const teleportRange = this.config.teleportRange ?? 5;
    const playerPos = this.getPlayerPosition();

    // Pick a random position near the player
    const angle = Math.random() * Math.PI * 2;
    const radius = teleportRange * (0.5 + Math.random() * 0.5);
    const newPos = playerPos.add(new Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius));

    this.mesh.setEnabled(true);
    this.mesh.position.copyFrom(newPos);
    this.snapToGround();

    // Appear effect
    this.level.particles.createGlitterParticleSystem(this.mesh, false);
    this.level.game.sound.play('collectible', this.mesh);

    this.lookAt(playerPos);
    this.state = 'attacking';
    this.timer = this.attackDuration;
  }

  private teleportAway(): void {
    if (!this.level) return;
    this.level.particles.createDisappearanceParticleSystem(this.mesh);
    this.mesh.setEnabled(false);
  }

  clone(): EnemyTeleportBehavior {
    return new EnemyTeleportBehavior(this.startPosition, this.config);
  }
}
